import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Tooltip, Empty, Table, Tag } from 'antd';
import { useSelector } from 'react-redux';
import { beeXLogger } from '../../../../helpers/logger';
import { EventRanking } from '../EventRanking';
import { MenuOutlined } from '@ant-design/icons';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';

import './index.scss';
import { StateTag } from '../../../../components/StateTag';
import moment from 'moment';

const EVENT_FREQUENCY_DISCONNECTION_QUESTION_ID = 292;

const transformData = (rawData) =>
  rawData.map((item) => ({
    id: item.camera_serial,
    cameraSerial: item.camera_serial,
    cameraName: item.camera_name,
    gatewaySerial: item.gateway_serial,
    disconnectionCount: item.disconnection_count,
    currentStatus: item.current_status,
    earliestDisconnection: item.earliest_disconnection,
    latestDisconnection: item.latest_disconnection,
  }));

const columns = [
  {
    title: 'Name',
    dataIndex: 'cameraName',
    key: 'cameraName',
    ellipsis: { showTitle: false },
    render: (name, record) => (
      <Tooltip title={`${name} (${record.cameraSerial})`} placement="topLeft">
        <span className="camera-name">{name}</span>
      </Tooltip>
    ),
  },
  // {
  //   title: 'Serial',
  //   dataIndex: 'cameraSerial',
  //   key: 'cameraSerial',
  //   ellipsis: { showTitle: false },
  //   render: (serial) => (
  //     <Tooltip title={serial} placement="topLeft">
  //       <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{serial}</span>
  //     </Tooltip>
  //   ),
  // },
  {
    title: 'Status',
    dataIndex: 'currentStatus',
    key: 'currentStatus',
    render: (status) => <StateTag state={status === 'Offline' ? 0 : 1} />,
  },
  {
    title: 'Total',
    dataIndex: 'disconnectionCount',
    key: 'disconnectionCount',
    align: 'center',
    sorter: (a, b) => a.disconnectionCount - b.disconnectionCount,
  },
  {
    title: 'Earliest',
    dataIndex: 'earliestDisconnection',
    key: 'earliestDisconnection',
    render: (val) => <span>{moment(val).format('YYYY-MM-DD HH:mm:ss')}</span>,
  },
  {
    title: 'Latest',
    dataIndex: 'latestDisconnection',
    key: 'latestDisconnection',
    render: (val) => <span>{moment(val).format('YYYY-MM-DD HH:mm:ss')}</span>,
  },
];

const EventFrequencyDisconnection = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  selectedDate,
}) => {
  const [data, setData] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { namespace } = useSelector((state) => state);
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!isActive) setShowAll(false);
  }, [isActive]);

  const baseFetchData = useMonitoringDataFetch(
    EVENT_FREQUENCY_DISCONNECTION_QUESTION_ID,
    selectedDate,
  );

  const fetchEventFrequencyDisconnectionData = useCallback(async () => {
    if (!namespace) return null;

    try {
      setIsLoading(true);
      const rawData = await baseFetchData();
      console.log('🚀 ~ EventFrequencyDisconnection ~ rawData:', rawData);
      const transformedData = transformData(rawData);

      setData(transformedData);
      if (isMountedRef.current) {
        setIsLoading(false);
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = null;
        }
      }
      return transformedData;
    } catch (err) {
      beeXLogger.error(err);
      throw err;
    }
  }, [baseFetchData, namespace]);

  const { reload } = useMonitoringDataWithInterval(
    fetchEventFrequencyDisconnectionData,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(reload);
    }
  }, [reload, onRegisterReload]);

  useEffect(() => {
    if (isActive && isLoading && !loadingTimeout) {
      loadingTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isLoading) {
          setLoadingTimeout(true);
        }
      }, 30000);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isActive, isLoading, loadingTimeout]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    };
  }, []);

  const displayedData = showAll ? data : data.slice(0, 5);

  return (
    <EventRanking
      headerIcon={<MenuOutlined />}
      headerTitle="Top Event Frequency"
      headerSubTitle="Daily occurrence"
      showAll={showAll}
      onToggleAll={() => setShowAll(!showAll)}
      hasMore={data.length > 5}
    >
      <Table
        columns={columns}
        dataSource={displayedData}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          size: 'small',
        }}
        size="small"
        locale={{
          emptyText:
            loadingTimeout || !isLoading ? (
              <Empty description="No data" />
            ) : null,
        }}
        scroll={{ x: 'max-content' }}
        className="top-event-frequency-table"
      />
    </EventRanking>
  );
};

export default EventFrequencyDisconnection;
